import {useState, useCallback} from 'react';
import clsx from 'clsx';
import {Image, Link} from '@shopify/hydrogen';
import iconArrow from '~/assets/icons/icon-arrow.svg';
import iconMenu from '~/assets/icons/icon-menu.svg';
import iconClose from '~/assets/icons/icon-close.svg';
import {
  brands,
  mensSkis,
  skiTesters,
  womensSkis,
  TEST_PRODUCT_TYPES,
  nordicSkis,
} from '~/lib/const';

type SkiTestFilterType = {
  name: string;
  href?: string;
  children?: {name: string; href: string; exclude?: number[]}[];
};

export function SkiTestCategoriesResponsive(props: any) {
  const [expanded, setExpanded] = useState(false);
  const handleClickExpand = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);
  return (
    <>
      <div className="hidden md:block overflow-auto z-10 relative bg-white">
        <SkiTestCategories {...props} />
      </div>
      <div className="md:hidden bg-[#f5f5f5] p-2 rounded">
        <button
          className="my-2 flex items-center text-xl text-left"
          onClick={handleClickExpand}
        >
          <Image
            loading="lazy"
            src={expanded ? iconClose : iconMenu}
            width={32}
            height={32}
            alt={'menu'}
            className={clsx('transition-all pb-1', {})}
          />
          <span className="ml-3">Menu</span>
        </button>
        {expanded && (
          <div
            className={clsx(
              'overflow-hidden transition-max-height duration-500 ease-in-out',
            )}
          >
            <SkiTestCategories {...props} />
          </div>
        )}
      </div>
    </>
  );
}

function SkiTestCategoryItem({
  year,
  filter,
}: {
  year: number;
  filter: SkiTestFilterType;
}) {
  const [expanded, setExpanded] = useState(false);
  const isLink = filter.href != null;
  const WrapperComponent = isLink ? Link : 'button';

  const handleClickExpand = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const props = isLink
    ? {to: filter.href}
    : {
        type: 'button',
        'aria-label': `Toggle ${filter.name} filter`,
        onClick: handleClickExpand,
      };

  return (
    <div className={'border-t border-t-gray-300'}>
      {/*@ts-ignore*/}
      <WrapperComponent
        {...props}
        className={'flex w-full justify-between items-center py-5'}
      >
        <span className="font-header text-22/32 font-bold">{filter.name}</span>
        {!isLink && (
          <Image
            loading="lazy"
            src={iconArrow}
            width={16.58}
            height={9.71}
            alt={filter.name}
            className={clsx('transition-all mr-2', {
              'rotate-180': expanded,
            })}
          />
        )}
      </WrapperComponent>

      {expanded && (
        <div
          className={clsx(
            'overflow-hidden transition-max-height duration-500 ease-in-out',
          )}
        >
          <ul className="font-header text-18/30 font-bold space-y-2.5 mb-[22px] ml-4">
            {filter.children?.map((option) => {
              if (option.exclude?.includes(year)) return null;

              return (
                <div key={option.href}>
                  <Link to={option.href}>{option.name}</Link>
                </div>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function SkiTestCategories({
  year,
  type = TEST_PRODUCT_TYPES.SKI,
}: {
  year: number;
  type: string;
}) {
  const isNordicSkiTest = type === TEST_PRODUCT_TYPES.NORDIC_SKI;
  const skiFilters = isNordicSkiTest && nordicSkis(year);
  const menSkiFilters = mensSkis(year, type);
  const womenSkiFilters = womensSkis(year, type);
  const brandFilters = brands(year, type);
  return (
    <div>
      {skiFilters && (
        <SkiTestCategoryItem key={'skis'} filter={skiFilters} year={year} />
      )}
      {!isNordicSkiTest && menSkiFilters && (
        <SkiTestCategoryItem key={'mens'} filter={menSkiFilters} year={year} />
      )}
      {!isNordicSkiTest && womenSkiFilters && (
        <SkiTestCategoryItem
          key={'womens'}
          filter={womenSkiFilters}
          year={year}
        />
      )}
      {brandFilters && (
        <SkiTestCategoryItem key={'brands'} filter={brandFilters} year={year} />
      )}
      <SkiTestCategoryItem
        key={'testers'}
        filter={skiTesters(year, type)}
        year={year}
      />
    </div>
  );
}

export default SkiTestCategories;
